import { useEffect, useMemo, useRef, useState } from "react";
import {
  useJson, getJson, nf, plural, ago, fmtDate, card, control, sectionLabel, Chip, ConfidenceBadge, entityPath, sanctionPath, sanctionedPath, listingPath, countryPath, CATEGORY_BY_SLUG, SANCTION_BODY, Box,
} from "./shared";
import { ChoroplethMap, Tooltip, TableToggle, SimpleTable, vizTokens } from "./Charts";
import { useMeasure } from "./zoom";
import { StixBar, StixTypeTag } from "./Stix";

// Geopolitics › Sanctions (#/kb/sanctions): who sanctions whom, and why, from the lists of
// the US Treasury (OFAC), the UN Security Council, the EU, the UK, Australia and Canada (sanctions.js). The
// filters, the open country and the search are kept in the address (?body=eu&about=RU&
// kind=person&years=2022&country=CN&q=…), so a link opens the same view. A listing has its
// own page, #/kb/sanction/<id>.

const BODIES = ["us", "un", "eu", "uk", "au", "ca"];
const BODY_NAME = { us: "United States", un: "UN Security Council", eu: "European Union", uk: "United Kingdom", au: "Australia", ca: "Canada" };
// Colours: the reference categorical slots, validated with the dataviz validator on the site's
// card surfaces (#e5e7eb light, #252528 dark). By who sanctions: slots 1–6 (adjacent pairs pass in
// both modes, 2026-10-30). By what for: slots
// 1–7 for the biggest seven (fixed, so a colour always means the same thing), grey for the rest.
// Some light steps are under 3:1, so the legends are labelled and every chart has a table.
const BODY_COLOR = { us: ["#2a78d6", "#3987e5"], un: ["#eb6834", "#d95926"], eu: ["#1baf7a", "#199e70"], uk: ["#eda100", "#c98500"], au: ["#e87ba4", "#d55181"], ca: ["#008300", "#008300"] };
const ABOUT_COLOR = { RU: ["#2a78d6", "#3987e5"], IR: ["#eb6834", "#d95926"], terror: ["#1baf7a", "#199e70"], KP: ["#eda100", "#c98500"], narco: ["#e87ba4", "#d55181"], prolif: ["#008300", "#008300"], hr: ["#6250d6", "#9085e9"], rest: ["#8b8e94", "#6b6b70"] };
const KINDS = [["person", "People"], ["organisation", "Organisations"], ["ship", "Ships"], ["aircraft", "Aircraft"]];
const KIND_ONE = { person: "person", organisation: "organisation", ship: "ship", aircraft: "aircraft" };
const KEYS = ["body", "about", "kind", "years", "country", "q", "stack", "not"];
const PAGE = 50;

const regionNames = (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }); } catch { return null; } })();
const countryName = (iso2) => { if (iso2 === "XK") return "Kosovo"; try { return regionNames?.of(iso2) || iso2; } catch { return iso2; } };
const aboutName = (a, label) => label || (a === "rest" ? "Everything else" : /^[A-Z]{2}$/.test(a) ? countryName(a) : a);
const parseSpan = (v) => { const m = String(v || "").match(/^(\d{4})(?:-(\d{4}))?$/); if (!m) return null; const a = +m[1], b = +(m[2] || m[1]); return { from: Math.min(a, b), to: Math.max(a, b) }; };
const spanText = (sp, dash = "–") => (sp ? (sp.from === sp.to ? `${sp.from}` : `${sp.from}${dash}${sp.to}`) : "");
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });
const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;

function keepInHash(state) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  for (const k of KEYS) { if (state[k]) q.set(k, state[k]); else q.delete(k); }
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

const bodyColor = (ui) => { const dark = vizTokens(ui.B).dark; return (b) => BODY_COLOR[b][dark ? 1 : 0]; };
// The body's colour as a swatch, its name in text colour (a coloured label can be too faint to read).
export function BodyChip({ body, ui, children }) {
  const color = bodyColor(ui)(body);
  return (
    <Chip ui={ui} title={BODY_NAME[body]} style={{ color: ui.B.text, display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />{children || SANCTION_BODY[body]}
    </Chip>
  );
}

// A row's listings by who sanctions, as one bar of segments (2px gaps), sized to the biggest row.
function BodyBar({ byBody, max, ui, width = 180 }) {
  const color = bodyColor(ui);
  const total = BODIES.reduce((s, b) => s + (byBody[b] || 0), 0);
  const w = Math.max(2, Math.round((total / Math.max(1, max)) * width));
  let x = 0;
  const segs = BODIES.filter(b => byBody[b]);
  return (
    <svg width={width} height={10} style={{ display: "block", maxWidth: "100%" }} aria-hidden="true">
      {segs.map((b, i) => {
        const sw = Math.max(1, (byBody[b] / total) * w - (i < segs.length - 1 ? 2 : 0));
        const r = <rect key={b} x={x} y={0} width={sw} height={10} rx={i === segs.length - 1 ? 2 : 0} fill={color(b)} />;
        x += sw + 2;
        return r;
      })}
    </svg>
  );
}
function BodyCounts({ byBody, ui }) {
  const { B, fs } = ui;
  return (
    <span style={{ display: "inline-flex", gap: 8, flexWrap: "wrap", fontSize: fs(11), color: B.textMuted }}>
      {BODIES.filter(b => byBody[b]).map(b => <span key={b} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}><BodySwatch body={b} ui={ui} />{SANCTION_BODY[b]} {nf(byBody[b])}</span>)}
    </span>
  );
}
function BodySwatch({ body, ui }) {
  const color = bodyColor(ui)(body);
  return <span style={{ width: 9, height: 9, borderRadius: 2, background: color, display: "inline-block", flexShrink: 0 }} />;
}

// ─── The timeline: listings in force by the year they were added, stacked by who or what for ───

function Timeline({ rows, stack, onStack, span, onSpan, labels, ui }) {
  const { B, fs } = ui;
  const t = vizTokens(B);
  const box = useMeasure(900);
  const [tip, setTip] = useState(null);
  const [drag, setDrag] = useState(null);
  const [table, setTable] = useState(false);
  const thisYear = new Date().getFullYear();
  const keys = stack === "about" ? [...Object.keys(ABOUT_COLOR).filter(k => k !== "rest"), "rest"] : BODIES;
  const colorOf = (k) => (stack === "about" ? ABOUT_COLOR[k] : BODY_COLOR[k])[t.dark ? 1 : 0];
  const nameOf = (k) => (stack === "about" ? aboutName(k, labels[k]) : BODY_NAME[k]);
  const valueOf = (r, k) => (stack === "about" ? r.about[k] : r.body[k]) || 0;
  const first = rows.length ? rows[0].year : thisYear, last = Math.max(thisYear, rows.length ? rows[rows.length - 1].year : thisYear);
  const years = [];
  for (let y = first; y <= last; y++) years.push(y);
  const byYear = new Map(rows.map(r => [r.year, r]));
  const width = box.width, padL = 40, padR = 8, padT = 8, h = 170, axisH = 18;
  const band = (width - padL - padR) / Math.max(1, years.length), barW = Math.max(2, Math.min(22, band - 2));
  const max = Math.max(1, ...rows.map(r => r.total));
  const nice = (() => { const p = Math.pow(10, Math.floor(Math.log10(max))); const m = max / p; return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p; })();
  const yOf = (v) => padT + h - (v / nice) * h;
  const yearAt = (e) => { const r = e.currentTarget.getBoundingClientRect(); return Math.min(last, first + Math.max(0, Math.min(years.length - 1, Math.floor((e.clientX - r.left - padL) / band)))); };
  const endDrag = () => {
    if (!drag) return;
    const from = Math.min(drag.a, drag.b), to = Math.max(drag.a, drag.b);
    setDrag(null);
    if (from !== to) return onSpan({ from, to });
    if (!byYear.get(from)?.total) return undefined;
    return onSpan(span && span.from === from && span.to === from ? null : { from, to: from });
  };
  const shown = drag ? { from: Math.min(drag.a, drag.b), to: Math.max(drag.a, drag.b) } : span;
  const labelEvery = Math.max(1, Math.ceil(years.length / Math.max(2, Math.floor((width - padL) / 42))));
  const present = keys.filter(k => rows.some(r => valueOf(r, k)));
  const btn = (on) => ({ ...ui.HP.navBtn, padding: "4px 10px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: fs(11), color: B.textDim }}>Stack by</span>
        <span style={{ display: "inline-flex", gap: 4 }} role="group" aria-label="Stack the years by">
          <button onClick={() => onStack("")} style={btn(stack !== "about")} aria-pressed={stack !== "about"}>Who sanctions</button>
          <button onClick={() => onStack("about")} style={btn(stack === "about")} aria-pressed={stack === "about"}>What for</button>
        </span>
        {span && <button onClick={() => onSpan(null)} style={btn(true)} aria-label={`Showing ${spanText(span)}; show all years`}>{spanText(span)} ✕</button>}
        <span style={{ marginLeft: "auto" }}><TableToggle on={table} onChange={setTable} ui={ui} /></span>
      </div>
      {table ? (
        <SimpleTable head={["Year", ...present.map(nameOf), "All"]} rows={[...rows].reverse().map(r => [String(r.year), ...present.map(k => nf(valueOf(r, k))), nf(r.total)])} ui={ui} />
      ) : (
        <div ref={box.ref} style={{ position: "relative" }} onPointerLeave={() => setTip(null)}>
          <svg width={width} height={padT + h + axisH} style={{ display: "block", maxWidth: "100%", touchAction: drag ? "none" : "pan-y", userSelect: "none" }} role="img"
            aria-label={`Listings in force by the year they were added, stacked by ${stack === "about" ? "what they're for" : "who sanctions"}`}
            onPointerDown={(e) => { if (e.button !== 0 || e.pointerType === "touch") return; const y = yearAt(e); e.currentTarget.setPointerCapture(e.pointerId); setDrag({ a: y, b: y }); setTip(null); }}
            onPointerMove={(e) => { if (drag) { const y = yearAt(e); setDrag(d => (d ? { ...d, b: y } : d)); } }}
            onPointerUp={endDrag} onPointerCancel={() => setDrag(null)}>
            {[0, nice / 2, nice].map(v => (
              <g key={v}>
                <line x1={padL} x2={width - padR} y1={yOf(v)} y2={yOf(v)} stroke={v === 0 ? t.axis : t.grid} strokeWidth={1} />
                <text x={padL - 6} y={yOf(v) + 4} textAnchor="end" fontSize={fs(10)} fill={t.inkDim}>{nf(v)}</text>
              </g>
            ))}
            {shown && shown.from !== shown.to && <rect x={padL + (shown.from - first) * band} y={padT} width={(shown.to - shown.from + 1) * band} height={h} fill={B.primary} opacity={0.12} pointerEvents="none" />}
            {years.map((y, i) => {
              const r = byYear.get(y);
              const x = padL + i * band + (band - barW) / 2;
              const dim = shown && (y < shown.from || y > shown.to);
              let base = padT + h;
              const segs = r ? keys.filter(k => valueOf(r, k)) : [];
              return (
                <g key={y} opacity={dim ? 0.35 : 1}>
                  {segs.map((k, j) => {
                    const hh = (valueOf(r, k) / nice) * h, top = base - hh;
                    const gap = j < segs.length - 1 ? 2 : 0;
                    const el = j === segs.length - 1
                      ? (() => { const rr = Math.min(4, barW / 2, hh); return <path key={k} d={`M${x},${base} V${top + rr} Q${x},${top} ${x + rr},${top} H${x + barW - rr} Q${x + barW},${top} ${x + barW},${top + rr} V${base} Z`} fill={colorOf(k)} />; })()
                      : <rect key={k} x={x} y={top + gap} width={barW} height={Math.max(0, hh - gap)} fill={colorOf(k)} />;
                    base = top;
                    return el;
                  })}
                  {i % labelEvery === 0 && <text x={padL + i * band + band / 2} y={padT + h + 13} textAnchor="middle" fontSize={fs(10)} fill={t.inkDim}>{y}</text>}
                  <rect x={padL + i * band} y={padT} width={band} height={h + axisH} fill="transparent" style={{ cursor: r ? "pointer" : "default", outline: "none" }}
                    onPointerMove={() => { if (!drag) setTip({ x: padL + i * band + band / 2, y: r ? yOf(r.total) : padT + h, flip: i > years.length * 0.6, value: r ? plural(r.total, "listing") : "None in force", label: String(y), detail: r ? [...segs.map(k => `${nameOf(k)}: ${nf(valueOf(r, k))}`), span && span.from === y && span.to === y ? "Click to show all years" : "Click for this year, or drag across years"].join("\n") : null }); }}
                    {...(r ? { tabIndex: 0, role: "button", "aria-label": `${y}: ${plural(r.total, "listing")}. Show this year`, onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSpan(span && span.from === y && span.to === y ? null : { from: y, to: y }); } } } : {})} />
                </g>
              );
            })}
          </svg>
          <Tooltip tip={tip} ui={ui} />
        </div>
      )}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: fs(11), color: B.textMuted, marginTop: 6 }}>
        {present.map(k => <span key={k} style={{ display: "inline-flex", gap: 5, alignItems: "center" }}><span style={{ width: 10, height: 10, borderRadius: 3, background: colorOf(k) }} />{nameOf(k)}</span>)}
      </div>
    </div>
  );
}

// ─── Listings table ───

function Ties({ r, ui }) {
  const { getConf } = ui;
  return (
    <>
      {r.also?.length > 0 && <span title={`Also on the lists of: ${r.also.map(b => BODY_NAME[b]).join(", ")}`} style={{ display: "inline-flex", gap: 3 }}>{r.also.map(b => <BodyChip key={b} body={b} ui={ui}>also {SANCTION_BODY[b]}</BodyChip>)}</span>}
      {r.group && <a href={entityPath(r.group)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("intrusion-set").color} title="An ATT&CK group's name (matched by name)">{r.group.name}</Chip></a>}
      {r.wallets > 0 && <a href={sanctionedPath(r.id.replace(/^ofac-/, ""))} style={{ textDecoration: "none" }}><Chip ui={ui} title="Crypto addresses OFAC lists for it">{plural(r.wallets, "crypto address", "crypto addresses")}</Chip></a>}
      {r.ransomware && <a href="#/kb/sanctioned?tie=ransomware" style={{ textDecoration: "none" }}><Chip ui={ui} title="In one of Treasury's ransomware actions">ransomware</Chip></a>}
      {r.prolif && <a href={listingPath(r.id)} style={{ textDecoration: "none" }}><Chip ui={ui} title="Also on Missiles & Proliferation, with its network">weapons programmes</Chip></a>}
    </>
  );
}

function ListingsTable({ filters, ui, labels, placeholder }) {
  const { B, fs } = ui;
  const [q, setQ] = useState(filters.q || "");
  const [query, setQuery] = useState(filters.q || "");
  const [sort, setSort] = useState("newest");
  const [pages, setPages] = useState([]);
  const [st, setSt] = useState({ loading: true, error: "", total: 0 });
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { filters.onQuery?.(query); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const base = useMemo(() => {
    const u = new URLSearchParams();
    for (const k of ["body", "about", "kind", "years", "country", "notOn"]) if (filters[k]) u.set(k, filters[k]);
    if (query) u.set("q", query);
    if (sort === "oldest") u.set("sort", "oldest");
    return `/api/kb/sanctions/listings?${u}`;
  }, [filters.body, filters.about, filters.kind, filters.years, filters.country, filters.notOn, query, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let live = true;
    setSt(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}&offset=0&limit=${PAGE}`).then(d => { if (!live) return; setPages([d.items]); setSt({ loading: false, error: "", total: d.total, query }); }).catch(e => live && setSt({ loading: false, error: e.message, total: 0, query }));
    return () => { live = false; };
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = pages.flat();
  const more = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} aria-label="Search the listings by name" style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          <option value="newest">Sort: Newest listings</option>
          <option value="oldest">Sort: Oldest listings</option>
        </select>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim }}>{st.loading ? "Loading…" : `${plural(st.total, "listing")}${st.query ? ` matching “${st.query}”` : ""}`}{st.query && !st.loading && !st.total ? ". Not being listed here doesn't clear anyone: check the official searches too (OFAC's Sanctions List Search, the UN's, the EU's and the UK's)." : ""}</div>
      {st.error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{st.error}</div>}
      {items.length > 0 && (
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                {["Name", "Who", "What for", "Country", "Listed"].map(h => <th key={h} style={{ padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id}>
                  <td style={{ ...cell, maxWidth: 480 }}>
                    <a href={sanctionPath(r.id)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{r.name}</a>
                    {r.kind !== "organisation" && <span style={{ marginLeft: 6, fontSize: fs(10), color: B.textDim }}>{KIND_ONE[r.kind]}</span>}
                    {r.alt && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Also: {r.alt}</div>}
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}><Ties r={r} ui={ui} /></div>
                  </td>
                  <td style={cell}><BodyChip body={r.body} ui={ui} /></td>
                  <td style={{ ...cell, color: B.textMuted }}>{r.regimes.map(a => aboutName(a, labels[a])).join(", ")}</td>
                  <td style={{ ...cell, color: B.textMuted }}>{r.countries.length ? r.countries.slice(0, 3).map(countryName).join(", ") + (r.countries.length > 3 ? ` +${r.countries.length - 3}` : "") : "—"}</td>
                  <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.date ? fmtDate(r.date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.length < st.total && <button onClick={more} style={{ ...control(ui), cursor: "pointer", alignSelf: "flex-start" }}>Show {Math.min(PAGE, st.total - items.length)} more</button>}
    </div>
  );
}

// ─── The official words behind a regime ───

function RegimeDetail({ regime, ui }) {
  const { B, fs } = ui;
  if (!regime) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {regime.programs.map(p => (
        <div key={p.code} style={{ fontSize: fs(12), lineHeight: 1.5, color: B.textMuted }}>
          <span style={{ color: B.text, fontWeight: 700 }}>{p.title || p.basis}</span>
          {p.title && <span> · Executive Order {p.order}, {fmtDate(p.since)}</span>}
          {p.title && p.basis && !/^Executive Order \d+$/.test(p.basis) && <span style={{ color: B.textDim }}> · {p.basis}</span>}
          <span style={{ color: B.textDim }}> · {plural(p.count, "listing")}{p.code !== p.basis && !/^UN /.test(p.code) ? ` · ${p.code}` : ""}</span>
          {p.url && <> · <a href={p.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{/federalregister/.test(p.url) ? "the order" : "source"} ↗</a></>}
        </div>
      ))}
    </div>
  );
}

// ─── Who sanctions whom, and why ───

function WhoWhom({ table, ui, onAbout, onCountry, about }) {
  const { B, fs } = ui;
  const [open, setOpen] = useState(null);
  const [all, setAll] = useState(false);
  const max = Math.max(1, ...table.map(t => t.total));
  const rows = all ? table : table.slice(0, 14);
  return (
    <div>
      <div style={{ ...card(ui), overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
          <thead>
            <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
              {["Sanctioned over", "Who sanctions, and how many", "Why", "Oldest listing"].map(h => <th key={h} style={{ padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(t => {
              const on = open === t.about;
              const cell = { padding: "10px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top", background: about === t.about ? B.primary + "10" : "transparent" };
              return [
                <tr key={t.about}>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>
                    <button onClick={() => setOpen(on ? null : t.about)} aria-expanded={on} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: B.text, fontWeight: 800, fontSize: fs(13), display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <span style={{ color: B.textDim, fontSize: fs(10), width: 10 }}>{on ? "▾" : "▸"}</span>{aboutName(t.about, t.label)}
                    </button>
                    <div style={{ fontSize: fs(11), color: B.textDim, marginLeft: 16 }}>{t.theme ? "a cause" : "a country"}</div>
                  </td>
                  <td style={{ ...cell, minWidth: 200 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}><BodyBar byBody={t.byBody} max={max} ui={ui} /><span style={{ fontWeight: 700, color: B.text }}>{nf(t.total)}</span></div>
                    <BodyCounts byBody={t.byBody} ui={ui} />
                  </td>
                  <td style={{ ...cell, color: B.textMuted, lineHeight: 1.5, maxWidth: 520 }}>{t.why || "—"}</td>
                  <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{t.first ? t.first.slice(0, 4) : "—"}</td>
                </tr>,
                on && (
                  <tr key={`${t.about}-open`}>
                    <td colSpan={4} style={{ padding: "4px 12px 14px 28px", background: B.bg + "55" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {t.regimes.map(r => (
                          <div key={r.key}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 5 }}><BodyChip body={r.body} ui={ui}>{BODY_NAME[r.body]}</BodyChip><span style={{ fontSize: fs(11), color: B.textDim }}>{plural(t.byBody[r.body] || 0, "listing")} under the filters</span></div>
                            <RegimeDetail regime={r} ui={ui} />
                          </div>
                        ))}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button onClick={() => onAbout(about === t.about ? "" : t.about)} style={{ ...ui.HP.navBtn, padding: "5px 11px", fontSize: fs(12) }}>{about === t.about ? "Show every cause again" : "Show only these on the map, the years and the list"}</button>
                          {!t.theme && <button onClick={() => onCountry(t.about)} style={{ ...ui.HP.navBtn, padding: "5px 11px", fontSize: fs(12) }}>Open {aboutName(t.about)}: the sanctions on it and who's listed there</button>}
                        </div>
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
      {table.length > 14 && <button onClick={() => setAll(!all)} style={{ ...control(ui), cursor: "pointer", marginTop: 10 }}>{all ? "Show the biggest only" : `Show all ${table.length}`}</button>}
    </div>
  );
}

// ─── How the lists compare ───

// "the US's list", "Australia's list".
const theirs = (b) => (b === "au" || b === "ca" ? `${SANCTION_BODY[b]}'s` : `the ${SANCTION_BODY[b]}'s`);
const NOT_ON_LABEL = (v) => (v === "all" ? "On no other list" : `Not on ${theirs(v)} list`);
const gapText = (days) => (days >= 730 ? `${(days / 365).toFixed(1)} years` : days >= 60 ? `${Math.round(days / 30.4)} months` : `${days} day${days === 1 ? "" : "s"}`);

function Compare({ filters, ui, notOn, onNotOn }) {
  const { B, fs } = ui;
  const color = bodyColor(ui);
  const u = new URLSearchParams();
  for (const k of ["body", "about", "kind", "years"]) if (filters[k]) u.set(k, filters[k]);
  const { data, error, loading } = useJson(`/api/kb/sanctions/compare?${u}`);
  if (loading) return <div style={{ fontSize: fs(12), color: B.textDim }}>Loading…</div>;
  if (error) return <div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div>;
  if (!data.bodies.length) return <div style={{ fontSize: fs(12), color: B.textDim }}>No listings under these filters.</div>;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const pick = (body, v) => (
    <button onClick={() => onNotOn(body, v)} title={`Show these ${SANCTION_BODY[body]} listings below`} style={{ background: notOn === v && filters.body === body ? B.primary + "18" : "none", border: "none", borderRadius: 5, padding: "2px 6px", cursor: "pointer", font: "inherit", color: B.accent, fontWeight: 700 }}>
      {nf(v === "all" ? data.bodies.find(x => x.body === body).alone : data.bodies.find(x => x.body === body).total - data.bodies.find(x => x.body === body).alsoOn[v])}
    </button>
  );
  const pct = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : "–");
  // A body with no listings under the filters (the UN has no Russia programme) gets no column.
  const cols = BODIES.filter(b => data.bodies.some(x => x.body === b));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>What one list has and another doesn't</div>
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "left" }}>Listed by</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Listings</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>On no other list</th>
                {cols.map(b => <th key={b} style={{ padding: "9px 12px", fontWeight: 700, textAlign: "right" }}>Not on {theirs(b)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.bodies.map(x => (
                <tr key={x.body}>
                  <td style={{ ...cell, textAlign: "left" }}><BodyChip body={x.body} ui={ui}>{BODY_NAME[x.body]}</BodyChip></td>
                  <td style={{ ...cell, color: B.text, fontWeight: 700 }}>{nf(x.total)}</td>
                  <td style={cell}>{pick(x.body, "all")} <span style={{ color: B.textDim }}>{pct(x.alone, x.total)}</span></td>
                  {cols.map(b => <td key={b} style={cell}>{b === x.body ? <span style={{ color: B.textDim }}>–</span> : <>{pick(x.body, b)} <span style={{ color: B.textDim }}>{pct(x.total - x.alsoOn[b], x.total)}</span></>}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8, lineHeight: 1.5 }}>Click a number to list them below. The same person, company or ship is matched across lists by the UN reference, the IMO number or the name (and birth year, for people), so a listing spelled very differently on two lists counts as missing from one: read these as "no match found", not proof.</div>
      </div>
      {data.pairs.length > 0 && (
        <div>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>The same listing on two lists: who listed it first</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.pairs.map(p => {
              const of = p.aFirst + p.same + p.bFirst;
              const seg = (n, color, label) => n ? <span title={`${label}: ${nf(n)}`} style={{ flex: n, background: color, minWidth: 2 }} /> : null;
              return (
                <div key={`${p.a}${p.b}`} style={{ display: "grid", gridTemplateColumns: "minmax(110px, 140px) 1fr", gap: 12, alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}><BodyChip body={p.a} ui={ui} /><span style={{ color: B.textDim, fontSize: fs(11) }}>and</span><BodyChip body={p.b} ui={ui} /></div>
                  <div style={{ minWidth: 0 }}>
                    {of > 0 && (
                      <div style={{ display: "flex", height: 10, borderRadius: 3, overflow: "hidden", gap: 2, marginBottom: 4 }} role="img" aria-label={`${SANCTION_BODY[p.a]} first ${nf(p.aFirst)}, same day ${nf(p.same)}, ${SANCTION_BODY[p.b]} first ${nf(p.bFirst)}`}>
                        {seg(p.aFirst, color(p.a), `${SANCTION_BODY[p.a]} first`)}
                        {seg(p.same, B.textDim, "Same day")}
                        {seg(p.bFirst, color(p.b), `${SANCTION_BODY[p.b]} first`)}
                      </div>
                    )}
                    <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5 }}>
                      <b style={{ color: B.text }}>{nf(p.matched)}</b> on both.{" "}
                      {of > 0 ? <>{SANCTION_BODY[p.a]} first {pct(p.aFirst, of)}, the same day {pct(p.same, of)}, {SANCTION_BODY[p.b]} first {pct(p.bFirst, of)}{p.medianGapDays ? `; when apart, typically ${gapText(p.medianGapDays)}` : ""}.</> : "No dates to compare."}
                      {p.brexit > 0 && <span style={{ color: B.textDim }}> {nf(p.brexit)} UK listings dated 31 December 2020 (Brexit) left out.</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8, lineHeight: 1.5 }}>By each list's own date for the listing. A listing that was removed and added again carries its newer date, and the UK's lists start at Brexit, so dates before 2021 favour the others.</div>
        </div>
      )}
    </div>
  );
}

// ─── A country ───

const BREXIT = "When the Brexit transition ended, the UK's own sanctions regulations took over from the EU's and carried its listings over, so many UK listings are dated 31 December 2020.";
function DayRows({ days, ui }) {
  const { B, fs } = ui;
  return (
    <>
      {days.map(d => (
        <div key={`${d.date}-${d.body}`} style={{ marginBottom: d.note ? 10 : 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: fs(12) }}>
            <span style={{ minWidth: 92, color: B.textMuted, whiteSpace: "nowrap" }}>{fmtDate(d.date)}</span>
            <BodyChip body={d.body} ui={ui} />
            <span style={{ color: B.text, fontWeight: 700 }}>{nf(d.count)}</span>
          </div>
          {d.note && (
            <div style={{ fontSize: fs(11), color: B.textMuted, lineHeight: 1.5, margin: "4px 0 0 4px", paddingLeft: 10, borderLeft: `2px solid ${B.border}` }}>
              {d.note}{d.url && <> <a href={d.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent, whiteSpace: "nowrap" }}>Source ↗</a></>}
            </div>
          )}
        </div>
      ))}
      {days.some(d => d.date === "2020-12-31" && d.body === "uk") && <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.5, marginTop: 4 }}>{BREXIT}</div>}
    </>
  );
}

function CountryPanel({ cc, filters, labels, ui, onClose, onAbout }) {
  const { B, fs } = ui;
  const u = new URLSearchParams();
  for (const k of ["body", "about", "kind", "years"]) if (filters[k]) u.set(k, filters[k]);
  const { data, error, loading } = useJson(`/api/kb/sanctions/country/${cc}?${u}`);
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [cc]);
  const name = countryName(cc);
  return (
    <div ref={ref} style={{ ...card(ui), padding: "18px 20px", borderTop: `3px solid ${B.primary}`, display: "flex", flexDirection: "column", gap: 14, scrollMarginTop: 16 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
        <h3 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{name}</h3>
        {data && <span style={{ fontSize: fs(13), color: B.textMuted }}>{plural(data.here, "listing")} there{filters.about || filters.body || filters.kind || filters.years ? " (filtered)" : ""}</span>}
        <a href={countryPath(cc)} style={{ fontSize: fs(12), color: B.accent, fontWeight: 600, marginLeft: "auto" }}>Everything on {name} →</a>
        <button onClick={onClose} style={{ ...ui.HP.navBtn, padding: "4px 10px", fontSize: fs(12) }} aria-label={`Close ${name}`}>Close ✕</button>
      </div>
      {loading && <div style={{ fontSize: fs(12), color: B.textDim }}>Loading…</div>}
      {error && <div style={{ fontSize: fs(12), color: "#dc2626" }}>{error}</div>}
      {data && <>
        <div>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Sanctions on {name}</div>
          {data.sanctionsOn.length ? (
            <>
              {data.why && <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.6, marginBottom: 10, maxWidth: "110ch" }}>{data.why}</div>}
              <div style={grid(300)}>
                {data.sanctionsOn.map(s => (
                  <div key={s.body} style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 6, flexWrap: "wrap" }}>
                      <BodyChip body={s.body} ui={ui}>{BODY_NAME[s.body]}</BodyChip>
                      <span style={{ fontSize: fs(18), fontWeight: 900, color: B.text }}>{nf(s.total)}</span>
                      <span style={{ fontSize: fs(11), color: B.textDim }}>listings</span>
                    </div>
                    <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5, marginBottom: 8 }}>
                      {nf(s.here)} in {name}{s.elsewhereCountries ? `, the rest in ${plural(s.elsewhereCountries, "other country", "other countries")}` : ""}{s.elsewhere.length ? ` (${s.elsewhere.slice(0, 5).map(e => `${countryName(e.country)} ${nf(e.count)}`).join(", ")})` : ""}{s.noCountry ? `; ${nf(s.noCountry)} with no country given` : ""}.
                    </div>
                    <RegimeDetail regime={s.regime} ui={ui} />
                  </div>
                ))}
              </div>
            </>
          ) : <div style={{ fontSize: fs(12), color: B.textMuted }}>None of the six has a sanctions programme about {name}{filters.body ? " (among the lists picked)" : ""}. Its listings below are there under other programmes.</div>}
        </div>
        <div style={grid(300)}>
          <div>
            <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Who's listed there, and what for</div>
            {data.here ? <>
              <div style={{ marginBottom: 10 }}><BodyCounts byBody={data.byBody} ui={ui} /></div>
              {data.whose.slice(0, 12).map(w => (
                <button key={w.about} onClick={() => onAbout(filters.about === w.about ? "" : w.about)} style={{ display: "flex", width: "100%", gap: 8, alignItems: "center", background: filters.about === w.about ? B.primary + "14" : "none", border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", font: "inherit", textAlign: "left", marginBottom: 2 }} title={`Show only listings for ${aboutName(w.about, w.label)}`}>
                  <span style={{ flex: "0 0 150px", fontSize: fs(12), color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{aboutName(w.about, w.label)}</span>
                  <BodyBar byBody={w.byBody} max={data.whose[0].total} ui={ui} width={140} />
                  <span style={{ fontSize: fs(12), color: B.textMuted }}>{nf(w.total)}</span>
                </button>
              ))}
              {data.multi > 0 && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 8 }}>{nf(data.multi)} of them are on more than one body's list.</div>}
            </> : <div style={{ fontSize: fs(12), color: B.textMuted }}>No listings with an address or nationality there{filters.about || filters.body || filters.kind || filters.years ? " under these filters" : ""}.</div>}
          </div>
          {data.days.length > 0 && (
            <div>
              <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>Biggest days</div>
              <DayRows days={data.days} ui={ui} />
            </div>
          )}
        </div>
        <div>
          <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>The listings</div>
          <ListingsTable key={`${cc}|${filters.body}|${filters.about}|${filters.kind}|${filters.years}`} filters={{ ...filters, country: cc }} labels={labels} ui={ui} placeholder={`Search ${name}'s listings by name…`} />
        </div>
      </>}
    </div>
  );
}

// ─── #/kb/sanctions ───

export function KbSanctions({ ui, params = {} }) {
  const { B, fs } = ui;
  const [body, setBody] = useState(BODIES.includes(params.body) ? params.body : "");
  const [about, setAbout] = useState(params.about || "");
  const [kind, setKind] = useState(KINDS.some(k => k[0] === params.kind) ? params.kind : "");
  const [span, setSpan] = useState(() => parseSpan(params.years));
  const [country, setCountry] = useState(/^[A-Z]{2}$/.test(params.country || "") ? params.country : "");
  const [q, setQ] = useState(params.q || "");
  const [stack, setStack] = useState(params.stack === "about" ? "about" : "");
  const [notOn, setNotOn] = useState(["all", ...BODIES].includes(params.not) ? params.not : "");
  const years = spanText(span, "-");
  useEffect(() => { keepInHash({ body, about, kind, years, country, q: country ? "" : q, stack, not: notOn }); }, [body, about, kind, years, country, q, stack, notOn]);
  const u = new URLSearchParams();
  if (body) u.set("body", body);
  if (about) u.set("about", about);
  if (kind) u.set("kind", kind);
  if (years) u.set("years", years);
  const { data, error, loading } = useJson(`/api/kb/sanctions?${u}`);
  // Names for causes (countries are named by the browser).
  const labels = useMemo(() => Object.fromEntries((data?.abouts || []).concat(data?.table || []).filter(a => a.label).map(a => [a.about, a.label])), [data]);
  const cat = CATEGORY_BY_SLUG.sanctions;
  const st = data?.state;
  const filtered = !!(body || about || kind || years);
  const filters = { body, about, kind, years };
  const btn = (on) => ({ ...ui.HP.navBtn, padding: "6px 12px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const openCountry = (cc) => setCountry(cc || "");
  // A number in "How the lists compare": that body's listings another list lacks, listed below.
  const pickNotOn = (b, v) => {
    const off = body === b && notOn === v;
    setBody(off ? body : b); setNotOn(off ? "" : v); setCountry("");
    if (!off) setTimeout(() => document.getElementById("sanctions-listings")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px", flexWrap: "wrap" }}>
          <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{cat.label}</h2>
          <span style={{ fontSize: fs(13), color: ui.getConf("identity").color, fontWeight: 800 }}>{st?.count ? nf(st.count) : ""}</span>
          <StixTypeTag ui={ui} type="identity" />
        </div>
        <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 6px", maxWidth: "110ch", lineHeight: 1.6 }}>
          Sanctions freeze the money of, and bar dealings with, the people, companies, ships and aircraft a government lists. Each listing is under a programme about a country (Russia's war on Ukraine, Iran's nuclear programme…) or a cause (terrorism, cyber attacks…), so the lists say who sanctions whom, and why.
          This page puts six lists side by side: the US Treasury's (OFAC), the UN Security Council's, the EU's, the UK's, Australia's and Canada's. Open a row to see each one's reasons in its own words, pick a country on the map to see the sanctions on it and who's listed there, or search a name.
        </p>
        <div style={{ fontSize: fs(11), color: B.textDim }}>
          {st ? BODIES.map(b => { const s = st.bodies?.[b]; return <span key={b} style={{ marginRight: 12 }}>{SANCTION_BODY[b]} {s?.builtAt ? `read ${ago(s.builtAt)}` : s?.error ? `unavailable (${s.error})` : "loading…"}</span>; }) : "Loading…"}
          {st && " · refreshed daily · only listings in force (a delisted name leaves the list)"}
        </div>
      </div>

      {error && <div style={{ ...card(ui), padding: 16, color: "#dc2626", fontSize: fs(13) }}>{error}</div>}
      {loading && !data && <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>}
      {data?.loading && <div style={{ ...card(ui), padding: 16, color: B.textMuted, fontSize: fs(13) }}>{st?.error ? `The lists are unavailable (${st.error}).` : "Loading… the six lists arrive several minutes after the server starts."}</div>}

      {data && !data.loading && <>
        {/* Headline numbers: labelled by the filters of the answer shown, and dimmed while the next one loads. */}
        <div aria-busy={loading} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 14, opacity: loading ? 0.45 : 1, transition: "opacity 0.15s" }}>
          <Box title={(data.filters ? Object.values(data.filters).some(Boolean) : filtered) ? "Listings (filtered)" : "Listings in force"} ui={ui}>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 6 }}>{nf(data.total)}</div>
            <div style={{ fontSize: fs(11), color: B.textDim }}>people, companies, ships and aircraft</div>
          </Box>
          <Box title="Countries" ui={ui}>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 6 }}>{nf(data.countryCount)}</div>
            <div style={{ fontSize: fs(11), color: B.textDim }}>where the listed live, work or are based{data.noCountry ? `; ${nf(data.noCountry)} give no country (ships, mostly)` : ""}</div>
          </Box>
          <Box title="Newest listing" ui={ui}>
            <div style={{ fontSize: fs(26), fontWeight: 900, color: B.text, lineHeight: 1.15, marginBottom: 6 }}>{data.newest ? fmtDate(data.newest.date) : "—"}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: fs(11), color: B.textDim, alignItems: "center" }}>{data.newest?.bodies.map(n => <span key={n.body} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}><BodyChip body={n.body} ui={ui} />{plural(n.count, "listing")}</span>)}</div>
          </Box>
          <Box title={`Added in ${data.thisYear}`} ui={ui}>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 6 }}>{nf(data.added)}</div>
            <div style={{ fontSize: fs(11), color: B.textDim }}>still in force</div>
          </Box>
        </div>

        {/* Filters: one row, above everything they change */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }} role="group" aria-label="Who sanctions">
            <button onClick={() => setBody("")} style={btn(!body)} aria-pressed={!body}>All six</button>
            {BODIES.map(b => <button key={b} onClick={() => setBody(body === b ? "" : b)} style={btn(body === b)} aria-pressed={body === b} title={BODY_NAME[b]}>{SANCTION_BODY[b]}</button>)}
          </span>
          <select value={about} onChange={e => setAbout(e.target.value)} aria-label="Sanctioned over" style={control(ui)}>
            <option value="">Sanctioned over: anything</option>
            {(data.abouts || []).map(a => <option key={a.about} value={a.about}>{aboutName(a.about, a.label)} ({nf(a.count)})</option>)}
            {about && !(data.abouts || []).some(a => a.about === about) && <option value={about}>{aboutName(about, labels[about])}</option>}
          </select>
          <select value={kind} onChange={e => setKind(e.target.value)} aria-label="Type" style={control(ui)}>
            <option value="">People, organisations, ships, aircraft</option>
            {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {span && <button onClick={() => setSpan(null)} style={btn(true)} aria-label={`Listed in ${spanText(span)}; show all years`}>Listed {spanText(span)} ✕</button>}
          {notOn && <button onClick={() => setNotOn("")} style={btn(true)} aria-label={`${NOT_ON_LABEL(notOn)}; show all listings`}>{NOT_ON_LABEL(notOn)} ✕</button>}
          {(filtered || notOn) && <button onClick={() => { setBody(""); setAbout(""); setKind(""); setSpan(null); setNotOn(""); }} style={{ ...btn(false), marginLeft: "auto" }}>Clear filters</button>}
        </div>

        {country && <CountryPanel key={country} cc={country} filters={filters} labels={labels} ui={ui} onClose={() => setCountry("")} onAbout={setAbout} />}

        <Box title="Who sanctions whom, and why" ui={ui} right={note(ui, "a listing under two programmes counts under both")}>
          <WhoWhom table={data.table} ui={ui} about={about} onAbout={setAbout} onCountry={openCountry} />
        </Box>

        <Box title="How the lists compare" ui={ui} right={note(ui, filtered ? "under the filters above" : "all listings")}>
          <Compare filters={filters} ui={ui} notOn={notOn} onNotOn={pickNotOn} />
        </Box>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 14, alignItems: "start" }}>
          <Box title="Where the listed are" ui={ui} style={{ gridColumn: "span 2" }} right={note(ui, "by address and nationality; click a country")}>
            <ChoroplethMap mode="count" values={data.countries} numeric={data.numeric} names={Object.fromEntries(Object.keys(data.countries).map(c => [c, countryName(c)]))} unit="listings" ui={ui} onSelect={(iso2) => openCountry(iso2)} />
          </Box>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            <Box title="Most listed" ui={ui}>
              {Object.entries(data.countries).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([c, n]) => (
                <button key={c} onClick={() => openCountry(c)} style={{ display: "flex", width: "100%", gap: 8, background: country === c ? B.primary + "14" : "none", border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer", font: "inherit", textAlign: "left", fontSize: fs(12), color: B.text, marginBottom: 2 }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{countryName(c)}</span><span style={{ color: B.textMuted }}>{nf(n)}</span>
                </button>
              ))}
            </Box>
            <Box title="Biggest days" ui={ui}>
              <DayRows days={data.days.slice(0, 8)} ui={ui} />
            </Box>
          </div>
        </div>

        <Box title="By the year they were listed" ui={ui} right={note(ui, "listings still in force; click a year or drag across years")}>
          <Timeline rows={data.timeline} stack={stack} onStack={setStack} span={span} onSpan={setSpan} labels={labels} ui={ui} />
        </Box>

        {!country && (
          <div id="sanctions-listings" style={{ scrollMarginTop: 16 }}>
            <Box title="Search the listings" ui={ui} right={note(ui, notOn ? `${notOn === "all" ? "on no other list" : `not on ${theirs(notOn)} list`}, under the filters above` : filtered ? "under the filters above" : "all six lists")}>
              <ListingsTable key={`${body}|${about}|${kind}|${years}|${notOn}`} filters={{ ...filters, notOn, q, onQuery: setQ }} labels={labels} ui={ui} placeholder="Search a person, company, ship or aircraft…" />
            </Box>
          </div>
        )}

        <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
          Data: <a href="https://ofac.treasury.gov/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>OFAC's SDN list</a> (US Treasury);{" "}
          <a href="https://main.un.org/securitycouncil/en/content/un-sc-consolidated-list" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>the UN Security Council Consolidated List</a>;{" "}
          <a href="https://data.europa.eu/data/datasets/consolidated-list-of-persons-groups-and-entities-subject-to-eu-financial-sanctions" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>the EU's consolidated list of financial sanctions</a> (© European Union, reused under the Commission's reuse notice; regimes named as on the <a href="https://www.sanctionsmap.eu/#/main" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>EU Sanctions Map</a>);{" "}
          <a href="https://www.gov.uk/government/publications/the-uk-sanctions-list" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>the UK Sanctions List</a> (contains public sector information licensed under the Open Government Licence v3.0);{" "}
          <a href="https://www.dfat.gov.au/international-relations/security/sanctions/consolidated-list" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>Australia's Consolidated List</a> (Department of Foreign Affairs and Trade, CC BY 4.0);{" "}
          <a href="https://www.international.gc.ca/world-monde/international_relations-relations_internationales/sanctions/consolidated-consolide.aspx?lang=eng" target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>the Consolidated Canadian Autonomous Sanctions List</a> (Global Affairs Canada; a copy of the version published there, reproduced for non-commercial use). Canada's list gives no addresses or nationalities, so its listings count under what they're sanctioned over but not under a country.
          The "why" in plain words is ours; the official words are under each row, with the US executive orders' titles from the Federal Register.
          A listing's country is where its addresses and nationalities are, so a Chinese company buying for Russia's army counts under China, under a Russia programme. The same person or company on two lists is matched by the UN reference the EU, UK and Australia copy, a ship's IMO number, or the same name (and birth year, for people), which can be wrong.
        </div>
      </>}
    </div>
  );
}

// ─── #/kb/sanction/<id> ───

export function KbSanction({ id, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/sanction/${encodeURIComponent(id)}`);
  const color = bodyColor(ui)(data?.body || "us");
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>{error} <a href="#/kb/sanctions" style={{ color: B.accent }}>Back to Sanctions</a></div>;
  const line = (k, v) => (v ? <div style={{ fontSize: fs(12), marginBottom: 6, lineHeight: 1.55 }}><span style={{ color: B.textDim }}>{k}: </span><span style={{ color: B.text }}>{v}</span></div> : null);
  const what = data.kindName[0].toUpperCase() + data.kindName.slice(1);
  const reasonsLabel = data.body === "uk" ? "The UK's statement of reasons" : data.body === "un" ? "The UN's reasons for listing" : data.body === "eu" ? "The EU's grounds and other information" : "OFAC's remarks";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/sanctions" style={{ color: B.textDim, textDecoration: "none" }}>Geopolitics</a> / <a href="#/kb/sanctions" style={{ color: B.textDim, textDecoration: "none" }}>Sanctions</a> / {data.name}</div>
      <div style={{ ...card(ui), borderTop: `3px solid ${color}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type="identity" color={getConf("identity").color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: fs(11), color: B.textMuted, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>{what} · sanctioned by</span>
              <BodyChip body={data.body} ui={ui}>{data.bodyName}</BodyChip>
              {data.same.map(s => s.body).filter((b, i, a) => b !== data.body && a.indexOf(b) === i).map(b => <BodyChip key={b} body={b} ui={ui}>also {SANCTION_BODY[b]}</BodyChip>)}
            </div>
            <h1 style={{ fontSize: fs(22), fontWeight: 800, margin: "6px 0 6px", color: B.text, wordBreak: "break-word" }}>{data.name}</h1>
            <div style={{ fontSize: fs(12), color: B.textMuted }}>
              {data.date ? `Listed ${fmtDate(data.date)}` : "Listing date not given"}{data.countries.length ? ` · ${data.countries.map(countryName).join(", ")}` : ""} · {data.agency}{data.ref ? ` · ${data.ref}` : data.ent ? ` · entry ${data.ent}` : ""}
            </div>
          </div>
        </div>
        <StixBar apiKey={data.key} ui={ui} />
      </div>

      <div style={grid(400)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <Box title="Why it's sanctioned" ui={ui}>
            {data.why.map(w => (
              <div key={w.about} style={{ marginBottom: 10 }}>
                <a href={`#/kb/sanctions?about=${encodeURIComponent(w.about)}`} style={{ fontSize: fs(13), fontWeight: 800, color: B.text, textDecoration: "none" }}>{aboutName(w.about, w.label)}</a>
                {w.why && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginTop: 2 }}>{w.why}</div>}
              </div>
            ))}
            <div style={{ ...sectionLabel(ui), margin: "12px 0 6px" }}>Listed under</div>
            {data.programs.map(p => (
              <div key={p.code} style={{ fontSize: fs(12), lineHeight: 1.5, marginBottom: 6, color: B.textMuted }}>
                <span style={{ color: B.text, fontWeight: 700 }}>{data.body === "uk" ? data.regimeName || p.basis : p.title || p.basis}</span>
                {p.title && <> · Executive Order {p.order}, {fmtDate(p.since)}</>}
                {data.body === "us" && <span style={{ color: B.textDim }}> · OFAC tag [{p.code}]</span>}
                {p.url && <> · <a href={p.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{/federalregister/.test(p.url) ? "the order" : "source"} ↗</a></>}
              </div>
            ))}
            {data.regulation && line("EU legal act", <a href={data.regulation.url || "#"} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{data.regulation.title}{data.regulation.published ? `, ${fmtDate(data.regulation.published)}` : ""} ↗</a>)}
            {data.source && line("Designated by", data.source === "UN" ? "the UN (the UK applies it)" : data.source === "UK" ? "the UK itself" : data.source)}
            {data.imposed?.length > 0 && line("Sanctions imposed", data.imposed.join(", "))}
            {data.legal?.length > 0 && line("Legal authorities", data.legal.join("; "))}
          </Box>
          {(data.reasons || data.other) && (
            <Box title={reasonsLabel} ui={ui}>
              {data.reasons && <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.65, whiteSpace: "pre-line" }}>{data.reasons}</div>}
              {data.other && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginTop: data.reasons ? 10 : 0 }}>{data.other}</div>}
            </Box>
          )}
          {data.same.length > 0 && (
            <Box title="Also sanctioned by" ui={ui} right={note(ui, "the same listing on another body's list")}>
              {data.same.map(s => (
                <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, fontSize: fs(12), flexWrap: "wrap" }}>
                  <BodyChip body={s.body} ui={ui} />
                  <a href={sanctionPath(s.id)} style={{ color: B.text, fontWeight: 600 }}>{s.name}</a>
                  {s.date && <span style={{ color: B.textDim }}>{fmtDate(s.date)}</span>}
                  <span style={{ color: B.textDim }}>· {s.how}</span>
                  {s.confidence && <ConfidenceBadge confidence={s.confidence} ui={ui} />}
                </div>
              ))}
            </Box>
          )}
          {data.links?.length > 0 && (
            <Box title={`OFAC links it to (${data.links.length})`} ui={ui} right={note(ui, "OFAC's own record")}>
              {data.links.map((l, i) => (
                <div key={`${l.ent}-${i}`} style={{ fontSize: fs(12), marginBottom: 6, lineHeight: 1.5 }}>
                  <span style={{ color: B.textDim }}>{l.type}: </span>
                  {l.id ? <a href={sanctionPath(l.id)} style={{ color: B.text, fontWeight: 600 }}>{l.name}</a> : <span style={{ color: B.text }}>{l.name}</span>}
                </div>
              ))}
            </Box>
          )}
          {(data.group || data.wallets > 0 || data.ransomware || data.prolif) && (
            <Box title="Elsewhere on Idle Hour" ui={ui}>
              {data.group && <div style={{ fontSize: fs(12), marginBottom: 6 }}>An ATT&CK group by name: <a href={entityPath(data.group)} style={{ color: B.accent }}>{data.group.name}</a> <span style={{ color: B.textDim }}>(matched by name)</span></div>}
              {data.wallets > 0 && <div style={{ fontSize: fs(12), marginBottom: 6 }}><a href={sanctionedPath(data.ent)} style={{ color: B.accent }}>{plural(data.wallets, "crypto address", "crypto addresses")} OFAC lists for it, in Sanctioned Wallets →</a></div>}
              {data.ransomware && <div style={{ fontSize: fs(12), marginBottom: 6 }}><a href="#/kb/sanctioned?tie=ransomware" style={{ color: B.accent }}>In one of Treasury's ransomware actions →</a></div>}
              {data.prolif && <div style={{ fontSize: fs(12), marginBottom: 6 }}><a href={listingPath(data.id)} style={{ color: B.accent }}>Its network on Missiles & Proliferation →</a></div>}
            </Box>
          )}
          {data.addresses.length > 0 && (
            <Box title={`Addresses (${data.addresses.length})`} ui={ui}>
              {data.addresses.map((a, i) => <div key={i} style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 5, lineHeight: 1.5 }}>{a}</div>)}
            </Box>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          {data.aliases.length > 0 && (
            <Box title="Also known as" ui={ui}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{data.aliases.map(a => <Chip key={a} ui={ui}>{a}</Chip>)}</div>
            </Box>
          )}
          {(data.title || data.nationalities?.length > 0 || data.flag || data.features?.length > 0) && (
            <Box title="Details" ui={ui}>
              {line(data.kind === 0 ? "Position" : "Title", data.title)}
              {line("Nationality", data.nationalities?.join(", "))}
              {line("Flag", data.flag)}
              {(data.features || []).filter(([k]) => !/^(?:Nationality Country|Citizenship Country|Vessel Flag)$/.test(k)).map(([k, v], i) => <div key={i}>{line(k.replace(/\s*-\s*$/, ""), v)}</div>)}
            </Box>
          )}
          {data.ids.length > 0 && (
            <Box title="Identifiers" ui={ui}>
              {data.ids.map((x, i) => <div key={i} style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 4, wordBreak: "break-word" }}>{x}</div>)}
            </Box>
          )}
          <Box title="Sources" ui={ui}>
            {data.ofacUrl && <div style={{ fontSize: fs(12), marginBottom: 5 }}><a href={data.ofacUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>OFAC Sanctions List Search: entry {data.ent} ↗</a></div>}
            {data.unUrl && <div style={{ fontSize: fs(12), marginBottom: 5 }}><a href={data.unUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>The UN sanctions committee ↗</a></div>}
            <div style={{ fontSize: fs(12), marginBottom: 5 }}><a href={{ us: "https://ofac.treasury.gov/specially-designated-nationals-and-blocked-persons-list-sdn-human-readable-lists", un: "https://main.un.org/securitycouncil/en/content/un-sc-consolidated-list", eu: "https://data.europa.eu/data/datasets/consolidated-list-of-persons-groups-and-entities-subject-to-eu-financial-sanctions", uk: "https://www.gov.uk/government/publications/the-uk-sanctions-list" }[data.body]} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{data.listName} ↗</a></div>
            {data.body === "uk" && <div style={{ fontSize: fs(11), color: B.textDim }}>Contains public sector information licensed under the Open Government Licence v3.0.</div>}
            {data.body === "eu" && <div style={{ fontSize: fs(11), color: B.textDim }}>© European Union; reused under the Commission's reuse notice.</div>}
          </Box>
        </div>
      </div>
    </div>
  );
}
